import type { LoyaltyEventKind } from '@salonomy/types';
import { Pill, Spinner } from '@salonomy/ui';
import { useLoyaltyMember, useLoyaltyProgram } from '../../data/loyalty.js';
import { dateFormat } from '../../lib/locale.js';

const EVENT_LABEL: Record<LoyaltyEventKind, string> = {
  earn: 'Earned',
  redeem: 'Redeemed',
  expiry: 'Expired',
  adjustment: 'Adjusted',
};

/** A compact, human date for a ledger row (the createdAt is a full ISO datetime). */
const DATE_FMT = dateFormat({
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/**
 * WS-13 — a READ-ONLY loyalty strip on the client record: the member's DERIVED points balance (the
 * server's signed sum of their ledger — never recomputed here) plus a short, read-only history. No
 * controls: points are earned at the till (off receipts) and redeemed/adjusted on their own surfaces;
 * this is a glanceable summary on the profile.
 *
 * It renders NOTHING when the salon isn't running a loyalty program (program disabled / inert default),
 * so a salon that doesn't use loyalty never sees an empty widget. Gated on the program load so it stays
 * quiet until we know whether to show at all.
 */
export function LoyaltyBalanceStrip({ clientId }: { clientId: string }) {
  const { data: program } = useLoyaltyProgram();
  const enabled = program?.enabled === true;
  const { data: member, isLoading, isError } = useLoyaltyMember(enabled ? clientId : null);

  // Don't show anything unless the salon actually runs a program — no empty widget.
  if (!enabled) return null;

  const history = member?.history ?? [];
  const recent = history.slice(0, 5);

  return (
    <div className="client-detail__block">
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
      >
        <span className="slm-stat__label">Loyalty</span>
        {member ? (
          <Pill tone={member.balancePoints > 0 ? 'success' : 'neutral'}>
            {member.balancePoints} pts
          </Pill>
        ) : null}
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <Spinner size={14} />
          <span className="muted" style={{ fontSize: 12.5 }}>
            Loading balance…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          Couldn&rsquo;t load this client&rsquo;s loyalty balance.
        </div>
      ) : !member?.enrolled && history.length === 0 ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          Not enrolled yet — points start accruing on their next visit.
        </div>
      ) : recent.length === 0 ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          No points activity yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
          {recent.map((e) => (
            <div key={e.id} className="till__line" style={{ fontSize: 13 }}>
              <span className="muted">
                {EVENT_LABEL[e.eventKind]}
                {e.reason ? ` · ${e.reason}` : ''} · {DATE_FMT.format(new Date(e.createdAt))}
              </span>
              <span
                className="mono"
                style={{ color: e.points >= 0 ? 'var(--slm-success, inherit)' : 'inherit' }}
              >
                {e.points >= 0 ? '+' : ''}
                {e.points}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
